/**
 * Joycelyn Fashion — Circular Macro Magnifier (On-Click Only)
 * Magnifies ONLY inside the circular loupe lens on click (NOT on hover).
 * The base product photo remains 100% stable and unzoomed.
 */

class CircularMacroMagnifier {
  constructor(container) {
    this.container = container;
    this.img = container.querySelector('img.main-embroidery-img');
    this.lens = container.querySelector('.macro-zoom-lens');
    this.hint = container.querySelector('.zoom-hint-capsule span');
    this.highResSrc = container.getAttribute('data-highres-src') || (this.img ? this.img.src : '');

    this.isMagnifying = false;
    this.zoomFactor = 2.6;
    this.lensRadius = 90; // 180px diameter / 2

    if (!this.container || !this.img || !this.lens) return;
    this.init();
    this.initLightbox();
  }

  init() {
    // 1. CLICK TO ACTIVATE / TOGGLE MAGNIFIER (NOT on hover)
    this.container.addEventListener('click', (e) => {
      // Ignore if user clicked fullscreen lightbox trigger button
      if (e.target.closest('[data-open-macro-lightbox]')) return;

      this.isMagnifying = !this.isMagnifying;

      if (this.isMagnifying) {
        this.activate(e);
      } else {
        this.deactivate();
      }
    });

    // 2. MOUSE MOVE (Only moves lens when magnifier is ACTIVE)
    this.container.addEventListener('mousemove', (e) => {
      if (!this.isMagnifying) return;
      this.updatePosition(e.clientX, e.clientY);
    });

    // 3. MOUSE LEAVE (Dismisses magnifier when cursor exits container)
    this.container.addEventListener('mouseleave', () => {
      if (this.isMagnifying) {
        this.deactivate();
      }
    });

    // 4. TOUCH SUPPORT FOR MOBILE & TABLET
    this.container.addEventListener('touchstart', (e) => {
      if (e.target.closest('[data-open-macro-lightbox]')) return;
      if (e.touches.length === 1) {
        this.isMagnifying = !this.isMagnifying;
        if (this.isMagnifying) {
          this.activate(e.touches[0]);
        } else {
          this.deactivate();
        }
      }
    }, { passive: true });

    this.container.addEventListener('touchmove', (e) => {
      if (!this.isMagnifying) return;
      if (e.touches.length === 1) {
        this.updatePosition(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: true });
  }

  activate(eventOrTouch) {
    this.container.classList.add('is-magnifying');

    // Dynamically retrieve current image source in case thumbnail was changed
    const currentSrc = this.container.getAttribute('data-highres-src') || (this.img ? this.img.src : '');
    this.lens.style.backgroundImage = `url('${currentSrc}')`;

    if (this.hint) {
      this.hint.textContent = 'Click to dismiss circular lens · Move to inspect stitches';
    }

    if (eventOrTouch) {
      this.updatePosition(eventOrTouch.clientX, eventOrTouch.clientY);
    }
  }

  deactivate() {
    this.isMagnifying = false;
    this.container.classList.remove('is-magnifying');

    if (this.hint) {
      this.hint.textContent = 'Click image to inspect with circular macro lens';
    }
  }

  updatePosition(clientX, clientY) {
    const target = this.img || this.container;
    const rect = target.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();

    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));

    // Position the lens center relative to the container
    const lensX = clientX - containerRect.left;
    const lensY = clientY - containerRect.top;

    this.lens.style.left = `${lensX}px`;
    this.lens.style.top = `${lensY}px`;

    // Calculate background size and position so it zooms ONLY inside the circle
    const bgWidth = rect.width * this.zoomFactor;
    const bgHeight = rect.height * this.zoomFactor;

    const bgX = this.lensRadius - (x * this.zoomFactor);
    const bgY = this.lensRadius - (y * this.zoomFactor);

    this.lens.style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
    this.lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
  }

  initLightbox() {
    // Dedicated Fullscreen Lightbox button
    const lightboxTrigger = this.container.querySelector('[data-open-macro-lightbox]') ||
      this.container.closest('.couture-stage-wrapper')?.querySelector('[data-open-macro-lightbox]') ||
      document.querySelector('[data-open-macro-lightbox]');
    const openLightbox = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.deactivate();

      let modal = document.getElementById('macro-lightbox-modal');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'macro-lightbox-modal';
        modal.className = 'modal-overlay';
        modal.innerHTML = `
          <div class="modal-content glass-panel" style="max-width: 900px; padding: 20px; background: rgba(24, 21, 30, 0.96); border: 1px solid var(--color-purple); text-align: center;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; border-bottom: 1px solid rgba(146, 75, 201, 0.3); padding-bottom: 10px;">
              <span class="btn-glass-pill-purple" style="font-size: 0.7rem;">Archival Macro Inspection</span>
              <strong style="color: #FFFFFF; font-size: 0.95rem; letter-spacing: 0.05em;">24K Gold Bullion & Zardozi Needlecraft</strong>
              <button type="button" data-close-lightbox style="background: transparent; border: none; color: #FFFFFF; font-size: 1.5rem; cursor: pointer; line-height: 1;">&times;</button>
            </div>
            <div style="position: relative; max-height: 75vh; overflow: auto; border-radius: 12px;">
              <img id="macro-lightbox-img" src="${this.highResSrc || (this.img ? this.img.src : '')}" alt="Macro Embroidery Detail" style="width: 100%; height: auto; display: block; border-radius: 8px;" />
            </div>
            <p style="margin: 12px 0 0; font-size: 0.75rem; color: #dcd8c8;">
              Hand-embossed metallic bullion threads couched on pure silk velvet · Master Artisan Atelier Archive
            </p>
          </div>
        `;
        document.body.appendChild(modal);

        modal.addEventListener('click', (ev) => {
          if (ev.target === modal || ev.target.closest('[data-close-lightbox]')) {
            modal.classList.remove('is-active');
            document.body.style.overflow = '';
          }
        });
      }

      const modalImg = modal.querySelector('#macro-lightbox-img');
      const currentSrc = this.container.getAttribute('data-highres-src') || (this.img ? this.img.src : '');
      if (modalImg) {
        modalImg.src = currentSrc;
      }
      modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    };

    if (lightboxTrigger) {
      lightboxTrigger.addEventListener('click', openLightbox);
    }
  }
}

function initCircularMacroMagnifiers() {
  document.querySelectorAll('.macro-zoom-container').forEach(c => new CircularMacroMagnifier(c));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCircularMacroMagnifiers);
} else {
  initCircularMacroMagnifiers();
}
